import { useCreateUserMutation, useUpdateUserMutation } from '@/app/api/usersApiSlice';
import AddUserAdditionalDetailsForm from '@/app/shared-components/Forms/Users/AddUserAdditionalDetailsForm';
import AddUserInformationForm from '@/app/shared-components/Forms/Users/AddUserInformationForm';
import AddUserRoleSpecificForm from '@/app/shared-components/Forms/Users/AddUserRoleSpecificForm';
import {
  CreateEditUserPayload,
  GetUserLogoResponseResult,
  GetUserResponseResult,
} from '@/app/shared-components/Types/Users';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Button } from '@mui/material';
import { memo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { sanitize } from '@/utils/helpers';
import { Language } from 'app/shared-components/Types/Utils';
import { createSchema, Schema } from './config';
import AddFilesForm from '@/app/shared-components/Forms/AddFilesForm';
import { removeInlineStyles } from '@/utils/formatters';
import { showMessage } from 'app/store/fuse/messageSlice';
import { useDispatch, useSelector } from 'react-redux';
import { getUser, selectUser } from 'app/store/userSlice';
import { AnyAction } from '@reduxjs/toolkit';
import EntityLogo from '@/app/shared-components/Avatars/EntityLogo';
import { apiRoutes } from '@/app/apiConfig/apiRoutes';
import history from '@history';
import Icon from 'app/shared-components/Icon/Icon';

export interface ContentPropsType {
  userData?: GetUserResponseResult;
  userLogoData?: GetUserLogoResponseResult;
  id?: string;
}

const Content = ({ userData, userLogoData, id = undefined }: ContentPropsType) => {
  const user = useSelector(selectUser);
  const methods = useForm<Schema>({
    resolver: yupResolver(createSchema(id)),
    defaultValues: {
      ...userData,
      ...userData?.details,
      apiKey: userData?.apiKey,
      shortToken: userData?.shortToken,
      licenseeId: userData?.licenseeId ?? user.licenseeId,
      languageId: Language.English,
      roleId: userData?.role?.id,
    },
  });

  const dispatch = useDispatch();

  const { handleSubmit } = methods;
  const [createUser, { isLoading: createUserIsLoading }] = useCreateUserMutation();
  const [updateUser, { isLoading: updateUserIsLoading }] = useUpdateUserMutation();

  const transformToPayload = (data: Schema): CreateEditUserPayload => {
    const {
      contactType,
      details: { description },
      dripActive,
      email,
      languageId,
      licenseeId,
      name,
      title,
      password,
      passwordConfirm,
      paymentHandle,
      paymentNotes,
      phone,
      roleId,
      skip,
      workEmail,
      workPhone,
      maxLeads,
      salesLevel,
      payPercentage,
      referralCompanyId,
      w9,
      avatar,
      shortToken,
    } = data;

    const w9File = w9 as File | null | null | undefined;
    const avatarFile = avatar as File | null | undefined;

    return {
      email,
      phone: phone ? sanitize(phone) : phone,
      skip,
      dripActive,
      licenseeId,
      shortToken,
      password,
      passwordConfirm,
      roleId,
      referralCompanyId,
      details: {
        name,
        title,
        languageId,
        contactType,
        description: removeInlineStyles(description ?? ''),
        workEmail,
        workPhone,
        paymentNotes,
        paymentHandle,
        maxLeads,
        salesLevel,
        payPercentage,
      },
      w9: w9File,
      avatar: avatarFile,
    };
  };

  const createNewUser = async (data: CreateEditUserPayload) => {
    const result = await createUser(data);
    if ('error' in result) {
      return;
    }
    history.push(`${apiRoutes.users}/${result.data.id}`);
    dispatch(showMessage({ message: 'Successfully created', variant: 'success' }));
  };

  const updateCurrentUser = async (data: CreateEditUserPayload, id: string) => {
    const result = await updateUser({ user: data, userId: id });
    if ('error' in result) {
      return;
    }
    dispatch(showMessage({ message: 'Successfully updated', variant: 'success' }));
  };

  const onSubmit = async (schemaData: Schema) => {
    if (id) {
      await updateCurrentUser(transformToPayload(schemaData), id);
    } else {
      await createNewUser(transformToPayload(schemaData));
    }

    if (user.id === userData?.id) {
      await dispatch(getUser() as unknown as AnyAction);
    }
  };

  const downloadW9 = () => {
    if (userData?.details?.w9Url) {
      window.open(userData?.details?.w9Url, '_blank');
    }
  };

  return (
    <FormProvider {...methods}>
      <Box className='flex items-center justify-center'>
        <EntityLogo photoURL={userLogoData?.photoURL} shape='circle' />
      </Box>
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        className='flex flex-row  mx-auto w-4/5 gap-28'
        data-test-target='add-client-order-form'
      >
        <Box className='flex flex-col items-start justify-between w-full py-8'>
          <AddUserInformationForm userId={id} />
          <AddUserAdditionalDetailsForm />
        </Box>

        <Box className='flex flex-col items-start justify-between w-full py-8'>
          <AddUserRoleSpecificForm />
          <AddFilesForm
            fileInputs={[
              {
                id: 'avatar',
                fieldName: 'Profile Photo',
                acceptedTypes: ['.png', '.jpg', '.jpeg'],
              },
              {
                id: 'w9',
                fieldName: 'W9',
                acceptedTypes: ['.pdf'],
              },
            ]}
            previewFieldName='avatar'
            previewLabel='Avatar'
          />
          {userData?.details?.w9Url && (
            <Button
              size='medium'
              variant='contained'
              color='secondary'
              startIcon={<Icon name='fileDownload' />}
              onClick={downloadW9}
            >
              Download W9
            </Button>
          )}
          <LoadingButton
            size='large'
            loading={createUserIsLoading || updateUserIsLoading}
            type='submit'
            variant='contained'
            color='primary'
            className='self-end my-16'
          >
            Save
          </LoadingButton>
        </Box>
      </Box>
    </FormProvider>
  );
};

export default memo(Content);
